// src/pages/hotel-catalogue/HotelCatalogueCoveragePage.tsx
//
// What the hotel static-data mirror holds, per supplier and country, and how
// fresh it is. Read-only: cities are seeded once, hotels load on a city's first
// search, and the refresh job keeps both inside TBO's 15-day window.

import { useMemo, useState } from "react";
import { FiRefreshCw, FiSearch } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import {
  useHotelCatalogueCoverage,
  type HotelCatalogueCoverageRow,
} from "@/api/features/hotel-catalogue";

const DAY_MS = 24 * 60 * 60 * 1000;

function formatDate(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function percent(part: number, whole: number): string {
  if (!whole) return "—";
  return `${Math.round((part / whole) * 100)}%`;
}

/** Stale when a loaded city's hotel list is older than the refresh window. */
function isStale(row: HotelCatalogueCoverageRow, refreshDays: number): boolean {
  if (!row.oldestCityHotelsSyncedAt) return false;
  return Date.now() - new Date(row.oldestCityHotelsSyncedAt).getTime() > refreshDays * DAY_MS;
}

export default function HotelCatalogueCoveragePage() {
  const { data, isLoading, isError, error, refetch, isFetching } = useHotelCatalogueCoverage();
  const [q, setQ] = useState("");
  const [provider, setProvider] = useState<"ALL" | "TBO" | "TRIPJACK">("ALL");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.countries ?? []).filter(
      (row) =>
        (provider === "ALL" || row.provider === provider) &&
        (!term ||
          String(row.countryName ?? "").toLowerCase().includes(term) ||
          String(row.countryCode ?? "").toLowerCase() === term)
    );
  }, [data, q, provider]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => ({
          countries: acc.countries + (row.cities > 0 ? 1 : 0),
          cities: acc.cities + row.cities,
          citiesWithHotels: acc.citiesWithHotels + row.citiesWithHotels,
          hotels: acc.hotels + row.hotels,
          hotelsWithDetails: acc.hotelsWithDetails + row.hotelsWithDetails,
        }),
        { countries: 0, cities: 0, citiesWithHotels: 0, hotels: 0, hotelsWithDetails: 0 }
      ),
    [rows]
  );

  return (
    <AdminLayout>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Hotel Catalogue</h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
          <p className="mt-2 max-w-2xl text-sm text-gray-600">
            Supplier reference data (cities, hotel codes, photos) kept locally because hotel search APIs take hotel
            codes, not cities. Prices are always fetched live.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <FiRefreshCw className={isFetching ? "animate-spin" : ""} aria-hidden /> Refresh
        </button>
      </div>

      {data ? (
        <div className="mb-4 flex flex-wrap gap-2 text-xs">
          <StatusChip ok={data.autoloadEnabled} label={data.autoloadEnabled ? "Hotels load on first search" : "First-search load is OFF"} />
          <StatusChip
            ok={data.refreshEnabled}
            label={data.refreshEnabled ? `Auto-refresh every ${data.refreshDays} days` : "Auto-refresh is OFF (HOTEL_CATALOGUE_REFRESH_ENABLED)"}
          />
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Countries" value={totals.countries.toLocaleString()} />
        <Stat label="Cities" value={totals.cities.toLocaleString()} />
        <Stat
          label="Cities with hotels loaded"
          value={totals.citiesWithHotels.toLocaleString()}
          hint={percent(totals.citiesWithHotels, totals.cities)}
        />
        <Stat label="Hotels" value={totals.hotels.toLocaleString()} />
        <Stat
          label="Hotels with photos"
          value={totals.hotelsWithDetails.toLocaleString()}
          hint={percent(totals.hotelsWithDetails, totals.hotels)}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Country name or code…"
            aria-label="Filter countries"
            className="w-64 rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20"
          />
        </div>
        {(["ALL", "TBO", "TRIPJACK"] as const).map((key) => (
          <button
            key={key}
            onClick={() => setProvider(key)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
              provider === key ? "border-sky-500 bg-sky-50 text-sky-800" : "border-gray-300 bg-white text-gray-600"
            }`}
          >
            {key === "ALL" ? "All suppliers" : key === "TBO" ? "TBO" : "TripJack"}
          </button>
        ))}
      </div>

      {isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error?.message || "Could not load catalogue coverage."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-gray-50 text-xs text-gray-600">
                <tr className="border-b border-gray-200">
                  <th className="px-3 py-2 text-left font-medium">Country</th>
                  <th className="px-3 py-2 text-left font-medium">Supplier</th>
                  <th className="px-3 py-2 text-right font-medium">Cities</th>
                  <th className="px-3 py-2 text-right font-medium">Cities loaded</th>
                  <th className="px-3 py-2 text-right font-medium">Hotels</th>
                  <th className="px-3 py-2 text-right font-medium">With photos</th>
                  <th className="px-3 py-2 text-left font-medium">Last loaded</th>
                  <th className="px-3 py-2 text-left font-medium">Freshness</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-gray-500">
                      Loading coverage…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-10 text-center text-gray-500">
                      No catalogue data. Seed cities with <code>npm run hotels:seed-cities</code>.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const stale = data ? isStale(row, data.refreshDays) : false;
                    return (
                      <tr key={`${row.provider}:${row.countryCode}`} className="border-b border-gray-100">
                        <td className="px-3 py-2.5">
                          <span className="font-medium text-gray-900">{row.countryName || "—"}</span>
                          <span className="ml-1.5 text-xs text-gray-500">{row.countryCode}</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-700">{row.provider === "TRIPJACK" ? "TripJack" : "TBO"}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{row.cities.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                          {row.citiesWithHotels.toLocaleString()}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">{row.hotels.toLocaleString()}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
                          {percent(row.hotelsWithDetails, row.hotels)}
                        </td>
                        <td className="px-3 py-2.5 text-gray-700">{formatDate(row.hotelsLastSyncedAt)}</td>
                        <td className="px-3 py-2.5">
                          {row.provider === "TRIPJACK" ? (
                            // TripJack has no city lookup; its content is loaded by id-range scan.
                            <span className="text-xs text-gray-500">Loaded by ID scan</span>
                          ) : !row.citiesWithHotels ? (
                            <span className="text-xs text-gray-500">Loads on first search</span>
                          ) : stale ? (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                              Older than {data?.refreshDays} days
                            </span>
                          ) : (
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                              Fresh
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900">
        {value}
        {hint ? <span className="ml-1.5 text-xs font-normal text-gray-500">{hint}</span> : null}
      </p>
    </div>
  );
}

function StatusChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`rounded-full border px-2.5 py-1 ${
        ok ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
    >
      {label}
    </span>
  );
}
